import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Sunrise, Play } from 'lucide-react';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import { getComeback, type ComebackData } from '../services/api';
import CompletionWheel from './CompletionWheel';
import { trackEvent } from '../utils/analytics';

// Seuil d'absence : le bandeau ne s'affiche qu'au retour après plus de ABSENCE_THRESHOLD_HOURS
// sans aucune tentative d'exercice (tous modes confondus). Pour le voir en dev : mettre 0.
const ABSENCE_THRESHOLD_HOURS = 20;

interface ComebackBannerProps {
  onStartKpExercise: (nodeId: string, kpId: string) => void;
  isGenerating: boolean;
}

const dismissKey = (userId: string) => `comeback_dismissed_${userId}`;

const formatAbsence = (hours: number): string => {
  if (hours >= 48) return `il y a ${Math.floor(hours / 24)} jours`;
  const h = Math.max(1, Math.round(hours));
  return `il y a ${h} heure${h > 1 ? 's' : ''}`;
};

/**
 * B3 — Bandeau « bilan de retour » : affiché sur l'accueil quand l'utilisateur revient
 * après une absence. Rappelle où il en était, signale les concepts délaissés (avec
 * relance d'exercice en un clic) et montre la roue de complétion du mode courant.
 * La croix mémorise la dernière activité vue : le bandeau ne revient qu'à la
 * prochaine absence (nouvelle valeur de lastActivityAt).
 */
const ComebackBanner: React.FC<ComebackBannerProps> = ({ onStartKpExercise, isGenerating }) => {
  const { user } = useUser();
  const { learningMode } = useLearningMode();
  const [data, setData] = useState<ComebackData | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const shownTrackedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    getComeback(user.id, learningMode)
      .then(result => {
        if (cancelled || !result) return;
        setData(result);
      })
      .catch(() => {
        // Best-effort : pas de bandeau si l'appel échoue, l'accueil reste fonctionnel
      });

    return () => { cancelled = true; };
  }, [user, learningMode]);

  const visible = Boolean(
    user &&
    data &&
    data.lastActivityAt &&
    (data.hoursSinceLastActivity ?? 0) > ABSENCE_THRESHOLD_HOURS &&
    localStorage.getItem(dismissKey(user.id)) !== data.lastActivityAt &&
    !dismissed
  );

  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      trackEvent('comeback_banner_shown', { learningMode });
    }
  }, [visible, learningMode]);

  if (!visible || !user || !data) return null;

  const handleDismiss = () => {
    if (data.lastActivityAt) {
      localStorage.setItem(dismissKey(user.id), data.lastActivityAt);
    }
    setDismissed(true);
  };

  const markdownInline = {
    strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
    code: ({ children }: { children?: React.ReactNode }) => (
      <code className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
    ),
    p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>
  };

  return (
    <div className="glass dark:glass-dark rounded-xl p-5 relative overflow-hidden animate-fade-in">
      <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl -mr-24 -mt-24 pointer-events-none"></div>

      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
        title="Masquer ce bilan"
        aria-label="Masquer ce bilan"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center mb-1">
            <Sunrise className="w-5 h-5 mr-2 text-amber-500 shrink-0" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Bon retour, {user.pseudonym} !
            </h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-slate-300">
            Dernière activité {formatAbsence(data.hoursSinceLastActivity || 0)}.
            {data.lastExercise && (
              <>
                {' '}Vous en étiez à{' '}
                <span className="text-gray-800 dark:text-slate-100">
                  <ReactMarkdown components={markdownInline}>{data.lastExercise.title}</ReactMarkdown>
                </span>{' '}
                <span className={`inline-block px-1.5 py-0.5 rounded-full text-xs align-middle ${data.lastExercise.isCorrect
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                  {data.lastExercise.isCorrect ? 'Réussi' : 'À retravailler'}
                </span>
              </>
            )}
          </p>

          {data.neglectedKps.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Pas revus depuis un moment :
              </p>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {data.neglectedKps.map(kp => (
                  <button
                    key={kp.id}
                    onClick={() => onStartKpExercise(kp.nodeId, kp.id)}
                    disabled={isGenerating}
                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={`Relancer un exercice sur : ${kp.name}`}
                  >
                    <Play className="w-3 h-3 mr-1.5" />
                    {kp.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {data.kpTotals.total > 0 && (
          <div className="flex sm:flex-col items-center gap-3 sm:gap-1 shrink-0 sm:pr-8">
            <CompletionWheel completed={data.kpTotals.completed} total={data.kpTotals.total} size={72} strokeWidth={8} />
            <span className="text-xs text-gray-500 dark:text-gray-400 sm:mt-1 sm:text-center">
              {data.kpTotals.completed} / {data.kpTotals.total} points de<br className="hidden sm:block" /> connaissance validés
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComebackBanner;
